import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Button,
} from "@mui/material";
import ScheduleIcon from "@mui/icons-material/ScheduleOutlined";
import WorkIcon from "@mui/icons-material/WorkOutlineOutlined";
import TodayIcon from "@mui/icons-material/TodayOutlined";
import PendingActionsIcon from "@mui/icons-material/PendingActionsOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate } from "react-router-dom";
import { UserApi } from "../../../apis";
import {
  PageHeader,
  StatCard,
  DateRangeFilter,
  CUSTOM_RANGE,
  formatRange,
} from "../../../components/ui";
import {
  ChartCard,
  HoursTrendChart,
  HoursByProjectChart,
  StatusBreakdown,
} from "../../../components/charts";
import { useTimeAnalytics } from "../../../hooks/useTimeAnalytics";
import { getStoredUser, getDisplayName } from "../../../utils/auth";

const greeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
};

export default function UserDashboard() {
  const navigate = useNavigate();
  const [range, setRange] = useState(30);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [totals, setTotals] = useState(null);

  const user = getStoredUser();
  const isCustom = range === CUSTOM_RANGE;
  const a = useTimeAnalytics({
    days: isCustom ? 30 : range,
    userId: user?.id,
    from: isCustom && from && to ? from : null,
    to: isCustom && from && to ? to : null,
  });

  useEffect(() => {
    let cancelled = false;
    UserApi.getUserDashboardData()
      .then((res) => {
        if (!cancelled) setTotals(res.data.data);
      })
      .catch((err) => {
        console.error("Failed to load dashboard totals:", err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const dateSpan = formatRange(a.startDate, a.endDate);
  const rangeLabel = isCustom ? dateSpan : `Last ${range} days`;
  // Submitted but not yet approved — the figure a user actually checks.
  const pendingHours =
    a.byStatus.find((s) => String(s.status).toLowerCase() === "pending")?.hours || 0;

  const rangePicker = (
    <DateRangeFilter
      value={range}
      onChange={setRange}
      from={from}
      to={to}
      onFromChange={setFrom}
      onToChange={setTo}
    />
  );

  // First name only — the full name is already in the account menu.
  const firstName = getDisplayName(user).split(" ")[0];

  return (
    <Box>
      <PageHeader
        title={`${greeting()}, ${firstName}`}
        subtitle={dateSpan}
        actions={rangePicker}
      />

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}>
          <StatCard
            label="This week"
            value={a.loading ? "—" : a.thisWeekHours.toFixed(1)}
            unit="h"
            icon={TodayIcon}
            color="primary"
            loading={a.loading}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label={rangeLabel}
            value={a.loading ? "—" : a.totalHours.toFixed(1)}
            unit="h"
            icon={ScheduleIcon}
            color="teal"
            loading={a.loading}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Awaiting approval"
            value={a.loading ? "—" : pendingHours.toFixed(1)}
            unit="h"
            icon={PendingActionsIcon}
            color="warning"
            loading={a.loading}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Assigned projects"
            value={totals?.projects ?? "—"}
            icon={WorkIcon}
            color="violet"
            loading={!totals}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={8}>
          <ChartCard
            title="Your hours over time"
            loading={a.loading}
            isEmpty={!a.loading && a.totalHours === 0}
            height={280}
            emptyMessage="Log time on your timesheet and it will show up here."
            action={
              <Button
                size="small"
                endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                onClick={() => navigate("/user/time/add")}
              >
                Timesheet
              </Button>
            }
          >
            <HoursTrendChart data={a.byDay} height={280} />
          </ChartCard>
        </Grid>

        <Grid item xs={12} lg={4}>
          <ChartCard
            title="Timesheet status"
            loading={a.loading}
            isEmpty={!a.loading && a.byStatus.length === 0}
            height={200}
          >
            <StatusBreakdown data={a.byStatus} total={a.totalHours} />
          </ChartCard>
        </Grid>

        <Grid item xs={12}>
          <ChartCard
            title="Where your time went"
            loading={a.loading}
            isEmpty={!a.loading && a.byProject.length === 0}
            height={260}
          >
            <HoursByProjectChart data={a.byProject} height={260} />
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  );
}
